import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Heart, Target, Star, CheckCircle, Gift, Loader2, DollarSign, Mail, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DonationTier {
  amount: number;
  title: string;
  description: string;
  tier: string;
  popular?: boolean;
  exclusive?: boolean;
}

const DONATION_TIERS: Record<string, DonationTier> = {
  supporter: {
    amount: 20,
    title: "Supporter",
    description: "Receive credit recognition and become a shareholder with royalty participation in all documentary proceeds.",
    popular: true,
    tier: "supporter"
  },
  patron: {
    amount: 500,
    title: "Patron",
    description: "Premium patron tier with prominent credit, shareholder status, royalty participation, and exclusive updates on the documentary journey.",
    exclusive: true,
    tier: "patron"
  },
  executive: {
    amount: 5000,
    title: "Executive Producer",
    description: "Executive Producer credit in the documentary, VIP shareholder status with enhanced royalties, podcast interview opportunity, and exclusive behind-the-scenes access.",
    exclusive: true,
    tier: "executive"
  }
};

export const Fundraising = () => {
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustomLoading, setIsCustomLoading] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<number>(0);
  const [donorEmail, setDonorEmail] = useState("");
  const [donorName, setDonorName] = useState("");

  const openEmailDialog = (tier: string | null, amount: number) => {
    setSelectedTier(tier);
    setSelectedAmount(amount);
    setShowEmailDialog(true);
  };

  const handleDonationWithEmail = async () => {
    if (!donorEmail || !donorEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setShowEmailDialog(false);
    
    if (selectedTier) {
      setLoadingTier(selectedTier);
    } else {
      setIsCustomLoading(true);
    }

    try {
      const body = selectedTier 
        ? { tier: selectedTier, donorEmail, donorName }
        : { customAmount: selectedAmount, donorEmail, donorName };

      const { data, error } = await supabase.functions.invoke("create-donation", {
        body
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
        toast.success("Redirecting to checkout...");
      }
    } catch (error: any) {
      console.error("Donation error:", error);
      toast.error("Failed to process donation. Please try again.");
    } finally {
      setLoadingTier(null);
      setIsCustomLoading(false);
      setDonorEmail("");
      setDonorName("");
    }
  };

  const handleTierClick = (tier: string, amount: number) => {
    openEmailDialog(tier, amount);
  };

  const handleCustomDonation = () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount < 1) {
      toast.error("Please enter a valid amount (minimum $1)");
      return;
    }
    openEmailDialog(null, amount);
  };

  const tiers = Object.values(DONATION_TIERS);

  return (
    <>
      <section id="donate" className="py-20 px-4 bg-gradient-to-br from-background to-muted/30">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="bg-story-hope/10 text-story-hope border-story-hope/20 mb-4">
              <Gift className="w-4 h-4 mr-2" />
              BECOME A SHAREHOLDER
            </Badge>
            <h2 className="font-serif text-4xl md:text-5xl text-foreground mb-6">
              Choose Your Impact Level
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Every donation makes you a shareholder with royalty participation. Help us reach our $250,000 goal to bring Tachen's miracle reunion to life.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {tiers.map((reward, index) => (
              <motion.div
                key={reward.tier}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className={`h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  reward.tier === "executive" 
                    ? 'border-2 border-story-peace shadow-lg bg-gradient-to-br from-story-peace/5 to-transparent' 
                    : reward.popular 
                    ? 'border-2 border-story-hope shadow-lg' 
                    : 'border border-muted'
                }`}>
                  <CardHeader className="relative pb-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-4xl font-bold text-story-hope">
                        ${reward.amount.toLocaleString()}
                      </div>
                      <div className="flex flex-col gap-1">
                        {reward.popular && (
                          <Badge className="bg-story-hope text-black">
                            <Star className="w-3 h-3 mr-1" />
                            POPULAR
                          </Badge>
                        )}
                        {reward.tier === "executive" && (
                          <Badge className="bg-story-peace text-background">
                            VIP
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-2xl font-serif">{reward.title}</CardTitle>
                    <CardDescription className="text-base leading-relaxed min-h-[80px]">
                      {reward.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-story-hope" />
                      <span>Shareholder status included</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-story-hope" />
                      <span>Royalty participation</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-story-hope" />
                      <span>Exclusive insider video access</span>
                    </div>
                    {reward.tier === "executive" && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-story-hope" />
                        <span>Podcast interview opportunity</span>
                      </div>
                    )}
                    <Button 
                      onClick={() => handleTierClick(reward.tier, reward.amount)}
                      disabled={loadingTier !== null || isCustomLoading}
                      className={`w-full font-semibold transition-all duration-300 ${
                        reward.tier === "executive"
                          ? 'bg-story-peace hover:bg-story-peace/90 text-background shadow-lg hover:shadow-xl'
                          : reward.popular 
                          ? 'bg-story-hope hover:bg-story-hope/90 text-black shadow-lg hover:shadow-xl' 
                          : 'bg-primary hover:bg-primary/90'
                      }`}
                    >
                      {loadingTier === reward.tier ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Heart className="w-4 h-4 mr-2" />
                          Donate ${reward.amount.toLocaleString()}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Custom Amount */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12"
          >
            <Card className="max-w-lg mx-auto border-2 border-dashed border-story-hope/30">
              <CardContent className="p-8">
                <Target className="w-12 h-12 text-story-hope mx-auto mb-4" />
                <h3 className="text-2xl font-serif font-semibold text-center mb-2">Custom Amount</h3>
                <p className="text-muted-foreground text-center mb-6">
                  Every dollar helps us reach our $250,000 goal. All donors become shareholders with royalty participation.
                </p>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="number"
                      min="1"
                      placeholder="Enter amount"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="pl-10 h-12 text-lg"
                    />
                  </div>
                  <Button 
                    onClick={handleCustomDonation}
                    disabled={isCustomLoading || loadingTier !== null}
                    className="bg-story-hope hover:bg-story-hope/90 text-black font-semibold h-12 px-6"
                  >
                    {isCustomLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Heart className="w-5 h-5 mr-2" />
                        Donate
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Email Collection Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Almost There!</DialogTitle>
            <DialogDescription>
              Enter your email to receive your exclusive insider video access and donation confirmation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="donor-name" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Your Name
              </Label>
              <Input
                id="donor-name"
                placeholder="Enter your name"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="donor-email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="donor-email"
                type="email"
                placeholder="your@email.com"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                required
              />
            </div>
            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="text-muted-foreground">
                <strong className="text-foreground">You'll receive:</strong>
              </p>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                <li>✓ Exclusive insider video access</li>
                <li>✓ Donation confirmation</li>
                <li>✓ Shareholder documentation</li>
              </ul>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowEmailDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDonationWithEmail}
              className="flex-1 bg-story-hope hover:bg-story-hope/90 text-black font-semibold"
            >
              Continue to Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
